import { useLayoutEffect, useRef, useState } from 'react';
import { Accordion, Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  MarkLineComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import BasicECharts from '@/components/common/BasicEChart';
import type { EChartsOption } from 'echarts';
import { useParams } from 'react-router-dom';
import {
  useGetAnalysisCruxQuery
} from '@/app/PageSpeed/api/analysisApi';
import type {
  CruxFieldExperience,
  CruxHistogramBin,
  CruxMetric,
  CruxFieldDataHistory,
  CruxHistoryMetric,
  CruxCollectionPeriod,
  CruxReportData
} from '@/app/PageSpeed/api/types/analysisApiTypes';
import NoAnalysisData from '../components/NoAnalysisData';
import AnalysisIncompleteState from '../components/AnalysisIncompleteState';
import { isAnalysisFinished } from '../utils/analysisStatus';
import CruxPreload from '../components/CruxPreload';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadError,
  isNetworkApiError
} from '@/app/common/utils/apiErrorUtils';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import SubtleBadge from '@/components/common/SubtleBadge';
import ClusterViewModeSwitcher from '@/app/KeywordResearch/components/keyword-clustring/components/ClusterViewModeSwitcher';

const CRUX_COLORS = {
  good: '#0cce6b',
  ni: '#ffa400',
  poor: '#ff4e42'
};

echarts.use([
  LineChart,
  GridComponent,
  TooltipComponent,
  MarkLineComponent,
  CanvasRenderer
]);

const metricTitles: Record<string, string> = {
  lcp: 'Largest Contentful Paint (LCP)',
  inp: 'Interaction to Next Paint (INP)',
  cls: 'Cumulative Layout Shift (CLS)',
  fcp: 'First Contentful Paint (FCP)',
  ttfb: 'Time to First Byte (TTFB)'
};

const metricTooltips: Record<string, string> = {
  lcp:
    'زمان نمایش بزرگ‌ترین عنصر محتوایی صفحه (تصویر، ویدیو یا متن). این معیار سرعت بارگذاری محتوای اصلی را اندازه‌گیری می‌کند.',
  inp:
    'زمان پاسخ صفحه به تعامل کاربر مانند کلیک، لمس یا فشردن کلید. هرچه کمتر باشد، صفحه سریع‌تر به کاربر پاسخ می‌دهد.',
  cls:
    'میزان جابه‌جایی ناخواسته عناصر صفحه هنگام بارگذاری. مقدار کمتر یعنی چیدمان صفحه پایدارتر و تجربه کاربری بهتر.',
  fcp:
    'زمان نمایش اولین محتوای متنی یا تصویری روی صفحه. نشان می‌دهد کاربر چقدر سریع متوجه می‌شود صفحه در حال بارگذاری است.',
  ttfb:
    'زمان بین ارسال درخواست صفحه و دریافت اولین بایت پاسخ از سرور. معیاری برای سرعت پاسخ‌دهی سمت سرور است.'
};

const metricThresholds: Record<string, [number, number]> = {
  lcp: [2500, 4000],
  inp: [200, 500],
  cls: [0.1, 0.25],
  fcp: [1800, 3000],
  ttfb: [800, 1800]
};

const metricOrder = ['lcp', 'inp', 'fcp', 'cls', 'ttfb'];

const ratingColor = (rating?: string) => {
  if (rating === 'good') return CRUX_COLORS.good;
  if (rating === 'poor') return CRUX_COLORS.poor;
  return CRUX_COLORS.ni;
};

const ratingLabels: Record<string, string> = {
  good: 'خوب',
  'needs-improvement': 'نیازمند بهبود',
  poor: 'ضعیف'
};

const ratingBadgeBg = (rating?: string) => {
  if (rating === 'good') return 'success';
  if (rating === 'poor') return 'danger';
  return 'warning';
};

const ltrValue = (value: string) => `\u2066${value}\u2069`;

const formatMetricValue = (metricName: string, value?: number) => {
  if (value === undefined || value === null) return '—';
  if (metricName === 'cls') return value.toFixed(2);
  if (value >= 1000) {
    return ltrValue(`${(value / 1000).toFixed(1)} s`);
  }
  return ltrValue(`${Math.round(value)} ms`);
};

const formatThreshold = (metricName: string, value: number) => {
  if (metricName === 'cls') return value.toFixed(2);
  if (value >= 1000) {
    return ltrValue(`${(value / 1000).toFixed(1)} s`);
  }
  return ltrValue(`${value} ms`);
};

const getPinPosition = (
  value: number,
  metricName: string,
  bins: CruxHistogramBin[]
) => {
  const thresholds = metricThresholds[metricName];
  if (!thresholds?.length) return 50;

  const [goodMax, niMax] = thresholds;
  const poorMax = bins[2]?.end ?? niMax * 1.25;
  const goodPct = bins[0]?.density ?? 0;
  const niPct = bins[1]?.density ?? 0;
  const poorPct = bins[2]?.density ?? 0;

  let position: number;
  if (value <= goodMax) {
    const fraction = goodMax > 0 ? value / goodMax : 0;
    position = fraction * goodPct * 100;
  } else if (value <= niMax) {
    const fraction = (value - goodMax) / (niMax - goodMax);
    position = (goodPct + fraction * niPct) * 100;
  } else {
    const fraction = Math.min((value - niMax) / (poorMax - niMax), 1);
    position = (goodPct + niPct + fraction * poorPct) * 100;
  }

  return Math.min(Math.max(position, 1), 99);
};

const BAR_HEIGHT = 8;
const PIN_RADIUS = 4.5;
const PIN_GAP = 3;
const CHART_TOP = 36;

const DistributionBar = ({
  bins,
  p75,
  metricName,
  rating
}: {
  bins?: CruxHistogramBin[];
  p75?: number;
  metricName: string;
  rating?: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateWidth = () => setWidth(element.offsetWidth);
    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  if (!bins?.length || p75 === undefined) return null;

  const pinLeft = getPinPosition(p75, metricName, bins);
  const valueColor = ratingColor(rating);
  const pinX = (pinLeft / 100) * width;
  const barY = CHART_TOP;
  const pinCenterY = barY - PIN_RADIUS - 1;
  const valueText = formatMetricValue(metricName, p75);

  let segmentX = 0;
  const segments = bins.map((bin, index) => {
    const segmentWidth = (bin.density || 0) * width;
    const segment = {
      x: segmentX,
      width: segmentWidth,
      color:
        index === 0
          ? CRUX_COLORS.good
          : index === 1
            ? CRUX_COLORS.ni
            : CRUX_COLORS.poor
    };
    segmentX += segmentWidth;
    return segment;
  });

  return (
    <div
      ref={containerRef}
      className="w-100"
      style={{ marginBottom: 10, minHeight: barY + BAR_HEIGHT }}
    >
      {width > 0 && (
        <svg
          width={width}
          height={barY + BAR_HEIGHT}
          style={{ display: 'block', overflow: 'visible' }}
          aria-hidden
        >
          <text
            x={pinX}
            y={18}
            textAnchor="middle"
            direction="ltr"
            unicodeBidi="embed"
            fill={valueColor}
            style={{
              fontSize: '22px',
              fontWeight: 400,
              fontFamily:
                'Google Sans, Roboto, Helvetica Neue, Arial, sans-serif'
            }}
          >
            {valueText}
          </text>

          {segments.map((segment, index) => (
            <rect
              key={`segment-${index}`}
              x={segment.x}
              y={barY}
              width={segment.width}
              height={BAR_HEIGHT}
              fill={segment.color}
            />
          ))}

          <rect
            x={pinX - PIN_GAP / 2}
            y={barY}
            width={PIN_GAP}
            height={BAR_HEIGHT}
            fill="var(--bs-card-bg, #fff)"
          />

          <line
            x1={pinX}
            y1={pinCenterY + PIN_RADIUS}
            x2={pinX}
            y2={barY + BAR_HEIGHT}
            stroke="#3c4043"
            strokeWidth={1.5}
          />

          <circle
            cx={pinX}
            cy={pinCenterY}
            r={PIN_RADIUS}
            fill="#ffffff"
            stroke="#3c4043"
            strokeWidth={1.5}
          />
        </svg>
      )}
    </div>
  );
};

const BreakdownRows = ({
  metricName,
  bins
}: {
  metricName: string;
  bins?: CruxHistogramBin[];
}) => {
  const thresholds = metricThresholds[metricName];
  if (!bins?.length || !thresholds) return null;

  const [goodMax, niMax] = thresholds;
  const rows = [
    {
      label: `خوب (≤ ${formatThreshold(metricName, goodMax)})`,
      pct: bins[0]?.density ?? 0,
      color: CRUX_COLORS.good
    },
    {
      label: `نیازمند بهبود (${formatThreshold(metricName, goodMax)} - ${formatThreshold(metricName, niMax)})`,
      pct: bins[1]?.density ?? 0,
      color: CRUX_COLORS.ni
    },
    {
      label: `ضعیف (> ${formatThreshold(metricName, niMax)})`,
      pct: bins[2]?.density ?? 0,
      color: CRUX_COLORS.poor
    }
  ];

  return (
    <div className="mt-2">
      <div className="d-flex justify-content-end mb-1">
        <small className="text-600" style={{ fontSize: '0.7rem' }}>
          Page Loads
        </small>
      </div>
      {rows.map((row) => (
        <div
          key={row.label}
          className="d-flex justify-content-between align-items-baseline"
          style={{ fontSize: '0.78rem', lineHeight: 1.6 }}
        >
          <span style={{ color: row.color }}>{row.label}</span>
          <span style={{ color: row.color, minWidth: 36, textAlign: 'end' }}>
            {Math.round(row.pct * 100)}%
          </span>
        </div>
      ))}
    </div>
  );
};

const MetricCard = ({
  name,
  metric
}: {
  name: string;
  metric: CruxMetric;
}) => {
  const valueColor = ratingColor(metric.rating);

  return (
    <Card className="h-100 border">
      <Card.Body className="p-3 p-xl-4">
        <div
          className="d-flex align-items-center justify-content-between gap-2"
          style={{ marginBottom: '2.5rem' }}
        >
          <div className="d-flex align-items-center gap-2 min-w-0">
            <span
              className="flex-shrink-0 rounded-circle"
              style={{
                width: 8,
                height: 8,
                background: valueColor
              }}
            />
            <div className="d-flex align-items-center gap-1 min-w-0">
              <span
                className="text-900"
                style={{ fontSize: '0.85rem', lineHeight: 1.4 }}
              >
                {metricTitles[name] || name}
              </span>
              <HelpTooltip
                placement="top"
                html={metricTooltips[name] || 'معیار تجربه کاربر'}
              />
            </div>
          </div>
          <SubtleBadge
            pill
            bg={ratingBadgeBg(metric.rating)}
            className="flex-shrink-0 fs--2"
          >
            {ratingLabels[metric.rating || ''] || 'نامشخص'}
          </SubtleBadge>
        </div>

        <DistributionBar
          bins={metric.histogram}
          p75={metric.p75}
          metricName={name}
          rating={metric.rating}
        />

        <BreakdownRows metricName={name} bins={metric.histogram} />

        <div
          className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top"
          style={{ fontSize: '0.72rem', borderColor: 'rgba(128, 143, 166, .2)' }}
        >
          <span className="text-600 d-flex align-items-center gap-1">
            <FontAwesomeIcon icon="map-pin" className="opacity-75" />
            75th Percentile - {formatMetricValue(name, metric.p75)}
          </span>
          {name === 'ttfb' && (
            <span className="text-600 d-flex align-items-center gap-1">
              <FontAwesomeIcon icon="flask" />
              Experimental
            </span>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

const ExperiencePanel = ({
  title,
  icon,
  scopeLabel,
  experience
}: {
  title: string;
  icon: IconProp;
  scopeLabel: string;
  experience?: CruxFieldExperience | null;
}) => {
  const metrics = experience?.metrics || {};
  const available =
    experience?.available !== false && Object.keys(metrics).length > 0;
  const sortedMetrics = Object.entries(metrics).sort(
    ([first], [second]) =>
      metricOrder.indexOf(first) - metricOrder.indexOf(second)
  );

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3 px-1">
        <div className="d-flex align-items-center gap-2">
          <span
            className="d-flex align-items-center justify-content-center rounded-circle text-primary"
            style={{
              width: 34,
              height: 34,
              background: 'rgba(66, 133, 244, .13)'
            }}
          >
            <FontAwesomeIcon icon={icon} />
          </span>
          <div>
            <h5 className="mb-0 fw-bold">{title}</h5>
            <small className="text-600 d-block mt-1">
              {experience?.form_factor === 'PHONE'
                ? 'موبایل · اطلاعات کاربران واقعی'
                : 'اطلاعات کاربران واقعی'}
            </small>
          </div>
        </div>
        <span
          className={`badge rounded-pill bg-${
            available ? 'success' : 'secondary'
          }-subtle text-${available ? 'success' : 'secondary'}`}
        >
          <span>{available ? 'اطلاعات موجود است' : 'اطلاعاتی موجود نیست'}</span>
        </span>
      </div>
      {available ? (
        <Row className="g-3">
          {sortedMetrics.map(([name, metric]) => (
            <Col key={name} xs={12} md={4}>
              <MetricCard name={name} metric={metric} />
            </Col>
          ))}
        </Row>
      ) : (
        <Card>
          <Card.Body className="text-center py-5 text-600">
            <FontAwesomeIcon
              icon="chart-line"
              size="2x"
              className="mb-3 opacity-50"
            />
            <p className="mb-0">
              برای {scopeLabel} هنوز اطلاعات CrUX ثبت نشده است.
            </p>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

const historyMetricOrder = [
  'largest_contentful_paint',
  'interaction_to_next_paint',
  'first_contentful_paint',
  'cumulative_layout_shift',
  'time_to_first_byte'
];

const historyMetricAliases: Record<string, string> = {
  largest_contentful_paint: 'lcp',
  interaction_to_next_paint: 'inp',
  first_contentful_paint: 'fcp',
  cumulative_layout_shift: 'cls',
  time_to_first_byte: 'ttfb'
};

const historyMetricColor: Record<string, string> = {
  lcp: '#4285f4',
  inp: '#0f9d58',
  fcp: '#ab47bc',
  cls: '#f9ab00',
  ttfb: '#e37400'
};

const toHistoryValue = (value: number | string | null | undefined) => {
  const numericValue = typeof value === 'string' ? Number(value) : value;
  return numericValue !== null &&
    numericValue !== undefined &&
    Number.isFinite(numericValue)
    ? numericValue
    : null;
};

const formatHistoryDate = (
  date?: { day?: number; month?: number; year?: number }
) => {
  if (!date?.year || !date.month || !date.day) return '';
  return `${date.year}-${String(date.month).padStart(2, '0')}-${String(
    date.day
  ).padStart(2, '0')}`;
};

const formatHistoryValue = (metricKey: string, value: number) => {
  const metricAlias = historyMetricAliases[metricKey] || metricKey;
  if (metricAlias === 'cls') return value.toFixed(2);
  return value >= 1000
    ? `${(value / 1000).toFixed(1)} s`
    : `${Math.round(value)} ms`;
};

const HistoryMetricChart = ({
  metricName,
  metric,
  periods
}: {
  metricName: string;
  metric: CruxHistoryMetric;
  periods: CruxCollectionPeriod[];
}) => {
  const values = metric.percentilesTimeseries?.p75s?.map(toHistoryValue) || [];
  const dates = periods?.map(period => formatHistoryDate(period.lastDate)) || [];
  const validValues = values.filter(
    (value): value is number => value !== null
  );

  if (!validValues.length) return null;

  const metricAlias = historyMetricAliases[metricName] || metricName;
  const color = historyMetricColor[metricAlias] || '#4285f4';
  const thresholds = metricThresholds[metricAlias];
  const firstDateIndex = dates.findIndex(Boolean);
  const lastDateIndex = dates.reduce(
    (lastIndex, date, index) => (date ? index : lastIndex),
    -1
  );
  const firstDate = firstDateIndex >= 0 ? dates[firstDateIndex] : '';
  const lastDate = lastDateIndex >= 0 ? dates[lastDateIndex] : '';
  const option: EChartsOption = {
    animation: false,
    grid: { top: 24, right: 52, bottom: 58, left: 78 },
    tooltip: {
      trigger: 'axis',
      formatter: params => {
        const item = Array.isArray(params) ? params[0] : params;
        const index = Number(item?.dataIndex);
        const value = values[index];
        return value === null || value === undefined
          ? `${dates[index] || ''}<br/>اطلاعاتی موجود نیست`
          : `${dates[index] || ''}<br/>مقدار: ${formatHistoryValue(
              metricAlias,
              value
            )}`;
      }
    },
    xAxis: {
      show: true,
      type: 'category',
      data: dates,
      boundaryGap: false,
      axisLine: {
        show: true,
        lineStyle: { color: 'rgba(127, 147, 170, 0.32)', width: 1 }
      },
      axisTick: {
        show: true,
        alignWithLabel: true,
        lineStyle: { color: 'rgba(127, 147, 170, 0.32)' }
      },
      axisLabel: {
        show: true,
        color: 'rgba(127, 147, 170, 0.9)',
        fontSize: 10,
        hideOverlap: false,
        showMinLabel: true,
        showMaxLabel: true,
        rotate: 35,
        formatter: (value: string, index: number) =>
          index === firstDateIndex
            ? firstDate || value
            : index === lastDateIndex
              ? lastDate || value
              : ''
      }
    },
    yAxis: {
      show: true,
      type: 'value',
      scale: true,
      name:
        metricAlias === 'cls' ? '75th Percentile' : '75th Percentile (ms)',
      nameLocation: 'middle',
      nameRotate: 90,
      nameGap: 48,
      nameTextStyle: { color: 'rgba(127, 147, 170, 0.9)', fontSize: 10 },
      axisLine: {
        show: true,
        lineStyle: { color: 'rgba(127, 147, 170, 0.32)', width: 1 }
      },
      axisTick: {
        show: true,
        lineStyle: { color: 'rgba(127, 147, 170, 0.32)' }
      },
      splitLine: {
        show: true,
        lineStyle: { color: 'rgba(127, 147, 170, 0.32)', width: 1 }
      },
      axisLabel: {
        show: true,
        color: 'rgba(127, 147, 170, 0.9)',
        fontSize: 10,
        formatter: (value: number) => formatHistoryValue(metricAlias, value)
      }
    },
    series: [
      {
        type: 'line',
        data: values,
        connectNulls: false,
        smooth: false,
        symbol: 'circle',
        symbolSize: 5,
        itemStyle: { color },
        lineStyle: { color, width: 1.5 },
        areaStyle: { color: `${color}18` },
        markLine: thresholds
          ? {
              symbol: 'none',
              silent: true,
              lineStyle: { color: '#71869e', type: 'dashed' },
              data: [{ yAxis: thresholds[0] }, { yAxis: thresholds[1] }]
            }
          : undefined
      }
    ]
  };

  return (
    <Card className="h-100 border">
      <Card.Body className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="fw-semibold text-900 fs--1">
            روند تاریخی {metricAlias.toUpperCase()}
          </span>
        </div>
        <BasicECharts
          echarts={echarts}
          options={option}
          style={{ height: 280, width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

const CruxMetricsAccordion = ({
  history,
  currentMetrics
}: {
  history?: CruxFieldDataHistory | null;
  currentMetrics?: Record<string, CruxMetric>;
}) => {
  const record = history?.record;
  const periods = record?.collectionPeriods || [];
  const metrics = record?.metrics || {};
  const availableMetrics = historyMetricOrder.filter(
    name =>
      currentMetrics?.[historyMetricAliases[name]] ||
      metrics[name]?.percentilesTimeseries?.p75s?.length
  );

  if (!availableMetrics.length) return null;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3 px-1">
        <div>
          <h5 className="mb-1 fw-bold text-900">معیارهای CrUX</h5>
          <small className="text-600">
            اطلاعات فعلی و روند تاریخی هر معیار را مشاهده کنید
          </small>
        </div>
        <span className="text-600" style={{ fontSize: '0.75rem' }}>
          {record?.key?.formFactor === 'PHONE' ? 'موبایل' : 'دسکتاپ'}
        </span>
      </div>
      <Accordion
        alwaysOpen
        defaultActiveKey={availableMetrics.map(
          metricName => historyMetricAliases[metricName]
        )}
      >
        {availableMetrics.map(metricName => {
          const metricAlias = historyMetricAliases[metricName];
          const currentMetric = currentMetrics?.[metricAlias];
          const historyMetric = metrics[metricName];

          return (
            <Accordion.Item eventKey={metricAlias} key={metricName}>
              <Accordion.Header>
                {metricTitles[metricAlias] || metricName}
              </Accordion.Header>
              <Accordion.Body className="pb-3 bg-100">
                <Row className="g-3">
                  {currentMetric && (
                    <Col xs={12} xl={6}>
                      <MetricCard name={metricAlias} metric={currentMetric} />
                    </Col>
                  )}
                  {historyMetric?.percentilesTimeseries?.p75s?.length && (
                    <Col xs={12} xl={6}>
                      <HistoryMetricChart
                        metricName={metricName}
                        metric={historyMetric}
                        periods={periods}
                      />
                    </Col>
                  )}
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </div>
  );
};

const hasCruxExperience = (experience?: CruxFieldExperience | null) => {
  if (!experience || experience.available === false) return false;
  return Object.keys(experience.metrics || {}).length > 0;
};

const hasCruxHistory = (history?: CruxFieldDataHistory | null) => {
  const metrics = history?.record?.metrics;
  return Boolean(metrics && Object.keys(metrics).length > 0);
};

const hasUsableCruxData = (report?: CruxReportData | null) => {
  if (!report) return false;
  return (
    hasCruxExperience(report.field_data?.page) ||
    hasCruxExperience(report.field_data?.origin) ||
    hasCruxHistory(report.field_data_history)
  );
};

const NoCruxReport = () => (
  <Card className="text-center">
    <Card.Body className="py-5 px-5 px-md-7">
      <FontAwesomeIcon
        icon="chart-line"
        className="text-secondary mb-3 opacity-50"
        style={{ fontSize: '2rem' }}
      />
      <h5 className="text-1000 mb-2">داده‌ای برای گزارش CrUX یافت نشد</h5>
      <p className="text-700 fs--1 mb-0 mx-auto" style={{ maxWidth: '40rem' }}>
        برای این آدرس اطلاعات کاربران واقعی Chrome (CrUX) ثبت نشده است. این گزارش
        معمولاً وقتی ساخته می‌شود که صفحه یا دامنه ترافیک کافی از کاربران
        Chrome داشته باشد.
      </p>
    </Card.Body>
  </Card>
);

const Crux = () => {
  const { slug } = useParams<{ slug?: string }>();
  const [activeScope, setActiveScope] = useState<'page' | 'origin'>('origin');
  const access = useToolAccess('analysis');
  const {
    data: response,
    isFetching,
    isError,
    error,
    refetch
  } = useGetAnalysisCruxQuery(slug || '', {
    skip: access.isLoading || !access.haveAccess || !slug
  });

  if (access.isLoading) return <></>;
  if (!access.haveAccess) {
    return <ToolAccessDenied access={access} tool_label="ابزار واکاوی" />;
  }
  if (shouldShowDataLoadError(isError, error)) {
    return (
      <DataLoadError
        onRetry={() => refetch()}
        isRetrying={isFetching}
        isNetworkError={isNetworkApiError(error)}
      />
    );
  }
  if (isFetching && !response) {
    return <CruxPreload />;
  }

  const report = response?.data;
  if (!isAnalysisFinished(report?.status)) {
    return (
      <AnalysisIncompleteState
        status={report?.status}
        taskId={report?.task_id}
        onRefresh={() => refetch()}
      />
    );
  }
  if (!report) return <NoAnalysisData />;
  if (!hasUsableCruxData(report)) return <NoCruxReport />;
  const fieldData = report.field_data;

  return (
    <div>
      <div className="my-3 d-flex justify-content-center">
        <ClusterViewModeSwitcher
          value={activeScope}
          onChange={value => setActiveScope(value as 'page' | 'origin')}
          aria-label="محدوده اطلاعات CrUX"
          modes={[
            { id: 'page', icon: 'file-text', label: 'اطلاعات صفحه' },
            { id: 'origin', icon: 'diagram-3', label: 'اطلاعات دامنه' }
          ]}
        />
      </div>
      {activeScope === 'page' ? (
        <div className="mt-4">
          <ExperiencePanel
            title="اطلاعات صفحه"
            icon="file-alt"
            scopeLabel="این صفحه"
            experience={fieldData?.page}
          />
        </div>
      ) : (
        <div className="mt-4">
          {hasCruxExperience(fieldData?.origin) ||
          hasCruxHistory(report.field_data_history) ? (
            <CruxMetricsAccordion
              history={report.field_data_history}
              currentMetrics={fieldData?.origin?.metrics}
            />
          ) : (
            <ExperiencePanel
              title="اطلاعات دامنه"
              icon="diagram-3"
              scopeLabel="این دامنه"
              experience={fieldData?.origin}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Crux;
